import tw, { styled } from 'twin.macro';
import Image from 'next/image';
import { memo } from 'react';
import { useTranslation } from 'next-i18next';

import { Button } from '@restworld/ui-ds';
import { customImageLoader, getAssetSrc, useAnalytics } from '@restworld/utils-common';
import { useAuth } from '@restworld/feature-authentication';

import { useLoginSignupPopup } from '../../../../hooks';
import { useStaticImage } from '../../context/static-images';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface WhyRestworldProps {}

export const WhyRestworld = memo((props: WhyRestworldProps) => {
  const { onStartSignup } = useLoginSignupPopup();
  const { isAuthenticated } = useAuth();
  const { t: homePageTranslate } = useTranslation('home-page');
  const { t: commonTranslate } = useTranslation('common');

  const applyImg = useStaticImage('apply', getAssetSrc('/utils-assets/candidati.jpeg'));
  const recommendationImg = useStaticImage('recommendation', getAssetSrc('/utils-assets/posizione.png'));
  const trackingImg = useStaticImage('tracking', getAssetSrc('/utils-assets/punto.png'));
  const { trackEvent } = useAnalytics();

  const cards: CardProps[] = [
    {
      image: applyImg,
      header: commonTranslate('candidates'),
      description: homePageTranslate('know_function_very_well'),
    },
    {
      image: recommendationImg,
      header: homePageTranslate('purpose_you_positions'),
      description: `${homePageTranslate('jobs_most_on_the_line')} 😉.`,
    },
    {
      image: trackingImg,
      header: homePageTranslate('where_you_stand'),
      description: homePageTranslate('what_stage_are_you_in'),
    },
  ];

  return (
    <Wrapper>
      <Title>{homePageTranslate('another_recruit_platform')} 🥱</Title>
      <Header>{homePageTranslate('no_need_to_look_for_job')}</Header>
      <CardsContainer>
        {cards.map(({ image, header, description }, index) => (
          <Card {...{ image, header, description }} key={index} />
        ))}
      </CardsContainer>
      {!isAuthenticated && (
        <div tw="flex flex-col items-center w-full mt-10">
          <Button
            onClick={() => {
              onStartSignup();
              // trackEvent('initiate_create_account', {
              //   origin: 'homepage',
              // });
            }}
          >
            {commonTranslate('lets_begin')}? {commonTranslate('create_account')}
          </Button>
        </div>
      )}
    </Wrapper>
  );
});

type CardProps = {
  image: { src: string; placeholder?: 'blur'; blurDataURL?: string };
  header: string;
  description: string;
};

export const Card = ({ image, header, description }: CardProps) => {
  return (
    <CardWrapper>
      <CardImage>
        <StyledImage {...image} fill={true} alt="Image card" loader={customImageLoader} />
      </CardImage>
      <CardHeader>{header}</CardHeader>
      <CardDescription>{description}</CardDescription>
    </CardWrapper>
  );
};

const CardWrapper = styled.div(() => [
  tw`flex flex-col 2xl:w-[22.188rem] lg:w-[17rem]  w-[13.625rem] 2xl:min-w-[22.188rem] lg:min-w-[17rem] min-w-[13.625rem] `,
]);

const StyledImage = styled(Image)(() => [tw`absolute top-0 left-0 rounded-3.75 w-full h-full object-cover`]);

const CardHeader = styled.h5(() => [tw`text-black font-semibold leading-7.5 text-xl mt-8`]);

const CardImage = styled.div(() => [
  tw`relative flex flex-col w-full 2xl:h-[32.813rem]  lg:!h-[25.125rem] h-[18rem] box-border`,
]);

const CardDescription = styled.p(() => [tw`text-black text-base leading-6 mt-2`]);

const Wrapper = styled.div(() => [tw`flex flex-col w-full bg-gray-50 lg:py-20 2xl:px-0 lg:px-0 py-16 px-6  `]);

const Title = styled.h4(() => [
  tw`text-center lg:text-lg lg:leading-6.75 text-base  text-gray-600 font-medium uppercase`,
]);

const Header = styled.h2(() => [tw`text-black text-center lg:text-4xl  font-semibold leading-13.5 text-3xl mt-2`]);

const CardsContainer = styled.div(() => [
  tw`flex flex-row 2xl:justify-center lg:justify-center md:justify-center justify-start 2xl:gap-6 lg:gap-6 md:gap-6 lg:overflow-x-hidden  gap-8 w-full max-w-full overflow-x-auto scrollbar-none  mt-10`,
]);
